<template>
    <div ref="printContent" class="xx-print-content">
        <!-- @slot 使用配置时，支持使用prepend插槽在配置的表格前添加自定义内容 -->
        <slot name="prepend"></slot>
        <!-- @slot 不使用配置时，default插槽会插入自定义的内容 -->
        <slot>
            <table v-bind="$attrs">
                <tbody v-for="(bodyItem, index) in tableConfig" :key="index">
                    <tr v-for="(item, index2) in bodyItem.headerConfig" :key="`${index}1${index2}`">
                        <th
                            v-for="(v, vindex) in item"
                            :key="`${index}1${index2}${vindex}`"
                            v-bind="removeProperties(v, tdProperties.concat(['id', 'options']))"
                        >
                            <cell
                                ref="cell"
                                :data="reserveProperties(v, tdProperties)"
                                :tdData="v"
                                :detailInfo="detailInfo"
                                :slots="$slots"
                            ></cell>
                        </th>
                    </tr>
                    <tr v-for="(item, index2) in bodyItem.bodyConfig" :key="`${index}2${index2}`">
                        <td
                            v-for="(v, vindex) in item"
                            :key="`${index}2${index2}${vindex}`"
                            v-bind="removeProperties(v, tdProperties.concat(['id', 'options']))"
                        >
                            <cell
                                ref="cell"
                                :data="reserveProperties(v, tdProperties)"
                                :tdData="v"
                                :detailInfo="detailInfo"
                                :slots="$slots"
                            ></cell>
                        </td>
                    </tr>
                </tbody>
            </table>
        </slot>
        <!-- @slot 使用配置时，支持使用append插槽在配置的表格后添加自定义内容 -->
        <slot name="append"></slot>
        <div v-if="breakPage" class="break-page"></div>
    </div>
</template>
<script>
import Jsbarcode from 'jsbarcode';
import Cell from './Cell';

const isPromise = P => {
    return P && P.then && typeof P.then === 'function';
};

export default {
    name: 'XxPrintTemplate',
    components: {
        Cell
    },
    props: {
        /**
         * @Desc: tableConfig中类型为prop的单元格内容取值的对象
         * @type {object} detailInfo
         */
        detailInfo: {
            type: Object,
            default: () => {
                return {};
            }
        },
        /**
         * @Desc: cellConfig
         * @typedef {object} cellConfig
         * @property {String} text
         * @property {String} prop
         * @property {String} path
         * @property {String} slot
         */
        /**
         * @Desc: tbodyConfig
         * @typedef {object} tbodyConfig
         * @property {cellConfig[]} headerConfig
         * @property {cellConfig[]} bodyConfig
         */
        /**
         * @Desc: 使用表格配置时的表格配置
         * @type {tbodyConfig[]} tableConfig
         */
        tableConfig: {
            type: Array,
            default: () => {
                return [];
            }
        },
        /**
         * @Desc: 是否在末尾使用强制换行的标识符，主要用于在print-temp-group中使用，默认为true
         * @type {boolean} breakPage
         */
        breakPage: {
            type: Boolean,
            default: true
        },
        /**
         * @Desc: 打印前的回调函数，用于做一些隐藏的打印元素被渲染之后的操作，当返回值为false时会中止打印
         * @type {function} beforePrint
         * @return {(boolean | undefined | promise)} 返回值为false时会中止打印，异步回调需要返回promise
         */
        beforePrint: {
            type: Function,
            default: null
        },
        /**
         * @Desc: 打印后的回调函数
         * @type {function} afterPrint
         */
        afterPrint: {
            type: Function,
            default: null
        },
        /**
         * @Desc: 是否隐藏要打印的元素
         * @type {boolean} hidePrintInfo
         */
        hidePrintInfo: {
            type: Boolean,
            default: null
        },
        /**
         * @Desc: 自定义打印方法
         * @type {function} printMethod
         */
        printMethod: {
            type: Function,
            default: () => {
                window.print();
            }
        }
    },
    data() {
        return {
            tdProperties: ['text', 'prop', 'path', 'slot', 'code', 'template'],
            isFirstPrint: true,
            printElement: null
        };
    },
    computed: {
        __hidePrintInfo() {
            if (this.hidePrintInfo !== null) {
                return this.hidePrintInfo;
            }
            // !=
            if (this.$parent && this.$parent.hidePrintInfo != null) {
                return this.$parent.hidePrintInfo;
            }
            // 默认隐藏
            return true;
        }
    },
    mounted() {
        this.printElement = this.$refs.printContent;
        if (!this.__hidePrintInfo) {
            this.printElement.style.display = 'block';
            this.createBarcode();
        }
    },
    methods: {
        /**
         * @Desc: 暴露原生的jsbarcode方法，用户可以自己生成码
         */
        Jsbarcode,
        /**
         * @Desc: 执行打印的方法
         */
        handlePrint(vm = this) {
            this.$nextTick(() => {
                // 获取父节点和下面的节点
                let parentNode = vm.printElement.parentNode;
                let nextNode = vm.printElement.nextElementSibling;
                vm.showElement();
                // 可以在这里中止打印，返回false
                if (typeof vm.beforePrint === 'function') {
                    const handleResult = vm.beforePrint();
                    if (isPromise(handleResult)) {
                        handleResult
                            .then(() => {
                                this.createBarcode();
                                vm.printMethod();
                            })
                            .catch(e => {
                                vm.$message.error(e.message);
                            })
                            .finally(() => {
                                vm.handleAfterPrint(nextNode, parentNode);
                            });
                    } else {
                        if (handleResult !== false) {
                            this.createBarcode();
                            vm.printMethod();
                        }
                        vm.handleAfterPrint(nextNode, parentNode);
                    }
                } else {
                    this.createBarcode();
                    vm.printMethod();
                    vm.handleAfterPrint(nextNode, parentNode);
                }
            });
        },
        textToBase64Barcode(text, option) {
            // text: 要被转换的值， option: Jsbarcode 配置
            const canvas = document.createElement('canvas');
            Jsbarcode(canvas, text, option);
            return canvas.toDataURL('image/png');
        },
        handleAfterPrint(nextNode, parentNode) {
            if (this.__hidePrintInfo) {
                this.printElement.style.display = 'none';
            } else {
                document.body.removeChild(this.printElement);
                nextNode ? parentNode.insertBefore(this.printElement, nextNode) : parentNode.appendChild(this.printElement);
            }
            this.isFirstPrint = false;
            this.afterPrint && this.afterPrint();
        },
        showElement() {
            if (this.isFirstPrint || !this.__hidePrintInfo) {
                document.body.appendChild(this.printElement);
            }
            this.printElement.style.display = 'block';
        },
        removeProperties(obj, keys) {
            let result = {};
            Object.entries(obj).forEach(([key, value]) => {
                if (!keys.includes(key)) {
                    result[key] = value;
                }
            });
            return result;
        },
        reserveProperties(obj, keys) {
            let result = {};
            Object.entries(obj).forEach(([key, value]) => {
                if (keys.includes(key)) {
                    result[key] = value;
                }
            });
            return result;
        },
        createBarcode() {
            (this.$refs.cell || []).forEach(item => item.createBarcode());
        },
        getPropByPath(obj, path, strict) {
            let tempObj = obj;
            path = path.replace(/\[(\w+)\]/g, '.$1');
            path = path.replace(/^\./, '');

            let keyArr = path.split('.');
            let i = 0;
            for (let len = keyArr.length; i < len - 1; ++i) {
                if (!tempObj && !strict) break;
                let key = keyArr[i];
                if (key in tempObj) {
                    tempObj = tempObj[key];
                } else {
                    if (strict) {
                        throw new Error('please transfer a valid prop path to form item!');
                    }
                    break;
                }
            }
            return {
                o: tempObj,
                k: keyArr[i],
                v: tempObj ? tempObj[keyArr[i]] : null
            };
        }
    }
};
</script>
<style lang="scss">
@media print {
    body {
        position: static !important;
        overflow: visible !important;
        height: auto !important;
        padding: 0 !important;
    }
    .xx-print-content {
        page-break-inside: auto;
        tr {
            page-break-inside: avoid;
        }
    }
    body > div:not(.xx-print-content) {
        display: none !important;
    }
}
</style>
<style lang="scss" scoped>
.xx-print-content {
    display: none;
    font-family: ESim font;
    table {
        tbody {
            td,
            th {
                border: 1px solid #000;
            }
        }
    }
    .break-page {
        page-break-after: always;
    }
}
</style>
